import { GridDefinitionFactory } from '@b2b/common/factories/gridDefinition/types';
import { GridStateFactory } from '@b2b/common/factories/gridState/types';
import { NoticeFactory } from '@b2b/common/factories/notice';
import { CartService, CartServiceModule } from '@b2b/common/services';
import { PartnerDetail } from '@b2b/common/services/api/partnersApi/types';
import { ApiService } from '@b2b/common/services/api/types';
import { CalendarService } from '@b2b/common/services/calendar';
import { UserPreferencesService } from '@b2b/common/services/userPreferences/types';
import { ACMEListResponse, ACMEResponse, ColumnDef, ContentHeaderProps, extractACMEErrorMessage, extractACMEPayload } from '@b2b/types/core';
import { TODO } from '@b2b/types/core/utils';
import uiRouter, { StateService } from '@uirouter/angularjs';
import angular, { IFilterService, IHttpResponse, IScope, ITimeoutService } from 'angular';
import invariant from 'tiny-invariant';
import { PartnersDetailService, PartnersDetailServiceModule } from './partners-detail.service';
import { DateRangeComponentModule } from '@b2b/common/components/date-range';
import moment from 'moment-timezone';
import { PermissionsService } from '@b2b/common/services/permissions';
import { ThemesHelperService } from '@b2b/common/services/themesHelper';

PartnersDetailController.$inject = [
  '$scope',
  '$state',
  '$filter',
  '$timeout',
  'uuid2',
  'ApiService',
  'HelpersService',
  'CalendarService',
  'VenueService',
  'UserPreferencesService',
  'NoticeFactory',
  'PartnerData',
  'CategoriesData',
  'GridStateFactory',
  'GridDefinitionFactory',
  'CartService',
  'PartnersDetailService',
  'PermissionsService',
  'ThemesHelperService',
];

function PartnersDetailController(
  $scope: IScope & { partnersDetail: TODO },
  $state: StateService,
  $filter: IFilterService,
  $timeout: ITimeoutService,
  uuid2: TODO,
  ApiService: ApiService,
  HelpersService: TODO,
  CalendarService: CalendarService,
  VenueService: TODO,
  UserPreferencesService: UserPreferencesService,
  NoticeFactory: NoticeFactory,
  PartnerData: { data: ACMEResponse<PartnerDetail> },
  CategoriesData: { data: ACMEListResponse<TODO> },
  GridStateFactory: GridStateFactory,
  GridDefinitionFactory: GridDefinitionFactory,
  cartService: CartService,
  partnersDetailService: PartnersDetailService,
  permissionsService: PermissionsService,
  themesHelperService: ThemesHelperService,
) {
  const ctrl = {
    componentType: 'organization',

    model: {} as Partial<PartnerDetail>,
    accountCategories: [] as TODO[],
    otherAddresses: [] as TODO[],
    orders: {
      list: [],
      pagination: {
        count: 0,
        page: 1,
        pageSize: 50,
        sortDirection: 'asc',
        sortField: '',
      },
    } as ACMEListResponse<TODO>['payload'],

    attributes: [] as TODO[],
    memberships: [] as TODO[],

    isUpdatingCategory: false,
    isUpdatingPayment: false,

    isLoaded: false,

    notes: '',
    ordersSearch: '',
    accountCategory: '',
    selectedAccountCategory: null as { id: TODO } | null,
    getStatusLabel: VenueService.getStatusLabel,
    dateFormat: UserPreferencesService.getDateFormat('standardDate'),
    tab: 'orders',

    payment: {
      id: null as number | null,
      type: 'fullPayment',
      accountId: null as number | null,
      ticketDelivery: 'print',
      balanceDueDays: 0,
      depositDueDays: 0,
      depositMinimumAmount: 0,
      depositMinimumPercent: 0,
      uniqueVoucherNumbers: false,
      taxExempt: false,
    },

    details: {
      columns: 2,
      data: 'partnersDetail.model.company',
      include: [
        'name',
        'companyUrl',
        'phoneNumber',
        'streetAddress1',
        'streetAddress2',
        'city',
        'state',
        'zipCode',
        'country',
      ],
    },

    primaryContactDetails: {
      columns: 2,
      data: 'partnersDetail.model.primaryContact',
      include: [
        'firstName',
        'lastName',
        'email',
        'phoneNumber',
        'streetAddress1',
        'streetAddress2',
        'city',
        'state',
        'zipCode',
        'country',
      ],
    },

    secondaryContacts: [],

    secondaryContactsOptionsOverride: {
      paginationPageSize: 25,
      data: 'partnersDetail.secondaryContacts',
      appScopeProvider: {
        onDataGridPaginate: function (newPageNumber, newPageSize, sortField, sortDirection) {
          ctrl.onDataGridPaginate(newPageNumber, newPageSize, sortField, sortDirection);
        },
        onDataGridSortChange: function (grid, columns) {
          ctrl.onDataGridSortChange(grid, columns);
        },
      },
      columnDefs: [
        {
          field: 'id',
          displayName: 'ID',
          visible: false,
        },
        {
          field: 'externalId',
          displayName: 'External ID',
          visible: false,
        },
        {
          field: 'primary',
          displayName: 'Primary',
          visible: false,
          cellTemplate: `<div class="ui-grid-cell-contents"><span data-ng-show="row.entity.primary">Yes</span><span data-ng-hide="row.entity.primary">No</span></div>`,
        },
        {
          field: 'firstName',
          displayName: 'First Name',
        },
        {
          field: 'lastName',
          displayName: 'Last Name',
        },
        {
          field: 'email',
          displayName: 'Email',
        },
        {
          field: 'phoneNumber',
          displayName: 'Phone Number',
        },
        {
          field: 'addressId',
          displayName: 'Address ID',
          visible: false,
        },
        {
          field: 'streetAddress1',
          displayName: 'Address 1',
        },
        {
          field: 'city',
          displayName: 'City',
        },
        {
          field: 'state',
          displayName: 'State',
        },
        {
          field: 'country',
          displayName: 'Country',
        },
        {
          field: 'zipCode',
          displayName: 'Zip Code',
        },
      ] as ColumnDef[],
    },

    viewId: '',
    ordersGridState: new GridStateFactory(),
    contactsGridState: new GridStateFactory(),
    gridDefinition: new GridDefinitionFactory(),
    gridDefinitionContacts: new GridDefinitionFactory(),
    partnersDetailOrdersListGridName: $state.current.name + '.grid0',
    partnersDetailContactsListGridName: $state.current.name + '.grid1',

    ordersOptionsOverride: {
      paginationPageSize: 25,
      enableGridMenu: false,
      data: 'partnersDetail.orders.list',
      appScopeProvider: {
        onDataGridPaginate: function (newPageNumber, newPageSize, sortField, sortDirection) {
          ctrl.onOrdersDataGridPaginate(newPageNumber, newPageSize, sortField, sortDirection);
        },
        onDblClick: function (item) {
          ctrl.onOrdersDblClick(item);
        },
        onDataGridSortChange: function (grid, columns) {
          ctrl.onOrdersDataGridSortChange(grid, columns);
        },
      },
      columnDefs: [
        {
          field: 'view',
          displayName: 'View',
          width: 50,
          onCellNavClick: function (row, col) {
            ctrl.ordersOptionsOverride.appScopeProvider.onDblClick(row);
          },
          cellTemplate: `<div class="ui-grid-cell-contents"><a data-ng-click="grid.appScope.onDblClick(row)" role="link">View<span class="sr-only"> Details for Order No. {{ row.entity.orderNumber }}</span></a></div>`,
          visible: true,
          enableHiding: false,
          enableSorting: false,
          suppressSizeToFit: true,
        },
        {
          field: 'accountId',
          visible: false,
        },
        {
          field: 'orderId',
          visible: false,
        },
        {
          field: 'orderNumber',
          displayName: 'Order No.',
        },
        {
          field: 'totalAmount',
          displayName: 'Original Price',
          cellFilter: 'currency',
        },
        {
          field: 'discountedTotalAmount',
          displayName: 'Discounted Price',
          cellFilter: 'currency',
        },
        {
          field: 'orderBalance',
          displayName: 'Balance Due',
          cellFilter: 'currency',
        },
        {
          field: 'orderDate',
          displayName: 'Order Date',
          sort: {
            direction: 'desc',
          },
          cellFilter: 'date : "' + HelpersService.dateFormats.shortDate + '"',
        },
      ] as ColumnDef[],
    },

    membershipsGridState: new GridStateFactory(),

    membershipsGrid: {
      pageSize: 10,
      paginate: true,
      useExternalPagination: false,
      useExternalSorting: true,
      enableRowSelection: false,
      multiselect: false,
      showFooter: true,
      columns: [
        {
          field: 'view',
          displayName: 'View',
          width: 50,
          onCellNavClick: function (row, col) {
            ctrl.onMembershipsGridDoubleClick(row);
          },
          cellTemplate: `<div class="clickable ui-grid-cell-contents" data-ng-class="col.colIndex()"><a data-ng-click="grid.appScope.onGridDblClick(row)" role="link">View<span class="sr-only"> Details</span></a></div>`,
          visible: true,
          enableHiding: false,
          enableSorting: false,
        },
        {
          field: 'id',
          displayName: 'ID',
          visible: false,
        },
        {
          field: 'acmeMembershipNumber',
          displayName: 'Acme Membership Number',
          headerTooltip: true,
          visible: true,
        },
        {
          field: 'category',
          displayName: 'Category',
          headerTooltip: true,
          visible: true,
        },
        {
          field: 'program',
          displayName: 'Program',
          headerTooltip: true,
          visible: true,
        },
        {
          field: 'subcategory',
          displayName: 'Subcategory',
          headerTooltip: true,
          visible: true,
        },
        {
          field: 'email',
          displayName: 'Email',
          headerTooltip: true,
          visible: true,
        },
        {
          field: 'externalMembershipId',
          displayName: 'External Membership Id',
          headerTooltip: true,
          visible: true,
        },
        {
          field: 'importId',
          displayName: 'Import Id',
          headerTooltip: true,
          visible: true,
        },
        {
          field: 'offeringId',
          displayName: 'Offering Id',
          headerTooltip: true,
          visible: true,
        },
        {
          field: 'organizationId',
          displayName: 'Org. Id',
          headerTooltip: true,
          visible: true,
        },
        {
          field: 'organizationName',
          displayName: 'Org. Name',
          headerTooltip: true,
          visible: true,
        },
        {
          field: 'membershipCategory',
          displayName: 'Membership Category',
          headerTooltip: true,
          visible: true,
        },
        {
          field: 'membershipStanding',
          displayName: 'Membership Standing',
          headerTooltip: true,
          visible: true,
        },
        {
          field: 'isGift',
          displayName: 'Gift',
        },
      ] as ColumnDef[],
    },

    attributesGridState: new GridStateFactory(),

    attributeGridOptions: {
      showFooter: true,
      multiSelect: false,
      paginate: true,
      pageSize: 50,
      columnDefs: [
        {
          field: 'category',
          displayName: 'Category',
          sort: {
            direction: 'asc',
          },
        },
        {
          field: 'description',
          displayName: 'Description',
        },
        {
          field: 'notes',
          displayName: 'Notes',
        },
        {
          field: 'date',
          displayName: 'Date',
          cellFilter: 'date: "' + HelpersService.dateFormats.sortDate + '"',
          cellFormatter: function (item) {
            return CalendarService.toFormat(item.value, HelpersService.dateFormats.shortDateTime);
          },
        },
      ] as ColumnDef[],
    },

    notice: new NoticeFactory(),

    formsResponsesList: [] as TODO[],

    formsResponsesListStatus: 'initial' as  'initial' | 'pending' | 'fulfilled',

    formsFilterDateType: [
      { type: 'createdOn', name: 'Created On' },
      { type: 'updatedOn', name: 'Updated On' },
    ],

    formsSelectedFilterDateType: {} as { type: string; name: string },

    today: null as moment.Moment | null,
    minDate: null as moment.Moment | null,
    maxDate: null as moment.Moment | null,

    formsQueryParams: {
      filterType: '',
      startDate: '',
      endDate: '',
    },

    formsGridDefinition: new GridDefinitionFactory(),
    formsGridName: $state.current.name + '.grid-forms', 
    formsGridState: new GridStateFactory(),

    formsGridOptions: {
      optionsOverride: {
        appScopeProvider: {
          dateTimeFormat: this.dateTimeFormat,
          onDataGridPaginate: (newPage, pageSize, sortField, sortDirection) => {
            ctrl.formsGridState.updateGridState({ page: newPage, pageSize, sortField, sortDirection });

            ctrl.getPartnerForms();
          },
          onDataGridSortChange: (grid, columns, page) => {
            const data = columns[0];

            if (data) {
              ctrl.formsGridState.updateGridState({
                page,
                sortField: data.field,
                sortDirection: data.sort.direction,
              });

              ctrl.getPartnerForms();
            }
          },
          onGridDoubleClick: (row: { entity: TODO }) => {
            if (permissionsService.check('FORM_RESPONSE_VIEW')) {
              $state.go('application.orders.detail.forms.detail', {
                orderId: row.entity.orderId,
                responseId: row.entity.formResponseId,
              });
            }
          },
          viewOrder: (row: { entity: TODO }) => {
            $state.go('application.orders.detail.summary', {
              orderId: row.entity.orderId,
            });
          },
          viewItem: (row: { entity: TODO }) => {
            switch (row.entity.formCategoryType) {
              case 'event': {
                $state.go('application.programsInstance', { id: row.entity.itemId });
                break;
              }
              case 'membership': {
                $state.go('application.membership.detail', { id: row.entity.membershipId });
                break;
              }

              case 'donation':
              case 'addon':
              default: {
                break;
              }
            }

            return;
          },
        },
        rowHeight: 40,
        useAccessibleGrid: true,
        data: 'partnersDetail.formsResponsesList',
        columnDefs: [
          {
            field: 'createdOn',
            displayName: 'Create Date',
            visible: true,
            cellTemplate: `<div class="ui-grid-cell-contents">{{ row.entity.createdOn | formatDate: grid.appScope.dateTimeFormat }}</div>`,
          },
          {
            field: 'updatedOn',
            displayName: 'Update Date',
            visible: true,
            cellTemplate: `<div class="ui-grid-cell-contents">{{ row.entity.updatedOn | formatDate: grid.appScope.dateTimeFormat }}</div>`,
            sort: {
              direction: 'desc',
            },
          },
          {
            field: 'formCategoryType',
            displayName: 'Type',
            visible: true,
            colId: 'formCategoryType',
            enableSorting: false,
            cellTemplate: `<div class="ui-grid-cell-contents"><span class="text-capitalize">{{ row.entity.formCategoryType }}</span></div>`,
          },
          {
            field: 'itemName',
            displayName: 'Event Template Name',
            visible: true,
            colId: 'itemName',
            enableSorting: false,
            onCellNavClick: (row, col) => {
              this.formsGridOptions.optionsOverride.appScopeProvider.viewItem(row);
            },
            cellTemplate: `
              <div class="clickable ui-grid-cell-contents" ng-class="col.colIndex()">
                <a data-ng-click="grid.appScope.viewItem(row)" role="link">
                  <span class="sr-only">View Event </span>{{ row.entity.itemName }}<span class="sr-only"> Details</span>
                </a>
              </div
            `,
          },
          {
            field: 'membershipId',
            displayName: 'Membership ID',
            visible: true,
            colId: 'itemName',
            enableSorting: false,
            onCellNavClick: (row, col) => {
              this.formsGridOptions.optionsOverride.appScopeProvider.viewItem(row);
            },
            cellTemplate: `
              <div class="clickable ui-grid-cell-contents" ng-class="col.colIndex()">
                <a data-ng-click="grid.appScope.viewItem(row)" role="link">
                  <span class="sr-only">View Membership ID</span> {{ row.entity.membershipId }}<span class="sr-only"> Details</span>
                </a>
              </div>
            `,
          },
          {
            field: 'membershipLevel',
            displayName: 'Membership Level',
            visible: true,
            colId: 'membershipLevel',
            enableSorting: false,
          },
          {
            field: 'membershipOffering',
            displayName: 'Membership Offering',
            visible: false,
            colId: 'membershipOffering',
            enableSorting: false,
          },
          {
            field: 'membershipLifecycle',
            displayName: 'Membership Lifecycle',
            visible: false,
            colId: 'membershipLifecycle',
            enableSorting: false,
          },
          {
            field: 'formName',
            displayName: 'Form Name',
            visible: true,
            colId: 'formName',
            enableSorting: false,
          },
          {
            field: 'formNumber',
            displayName: 'Response Number',
            visible: true,
            colId: 'formNumber',
            enableSorting: false,
          },
          {
            field: 'orderId',
            displayName: 'Order ID',
            visible: true,
            colId: 'orderId',
            enableSorting: false,
            onCellNavClick: (row, col) => {
              this.formsGridOptions.optionsOverride.appScopeProvider.viewOrder(row);
            },
            cellTemplate: `
              <div class="clickable ui-grid-cell-contents" ng-class="col.colIndex()">
                <a data-ng-click="grid.appScope.viewOrder(row)" role="link">
                  View<span class="sr-only"> Order {{ row.entity.orderId }} Details</span>
                </a>
              </div>
            `,
          },
        ] as TODO[],
      },
    },

    init: function () {
      ctrl.viewId = $state.current.name ?? '';

      $scope.$emit('Application::contentTitleChange', {
        title: 'Organization Details',
        buttonRight: {
          label: 'Edit Organization',
          permissions: ['ACCT_UPDATE'],
          action: () => {
            $state.go('application.partners.edit', { accountId: ctrl.model.company?.id });
          },
        },
      } as ContentHeaderProps);

      if (PartnerData.data && PartnerData.data.success) {
        ctrl.model = PartnerData.data.payload;
        ctrl.payment.accountId = ctrl.model.company?.id ?? null;
        ctrl.notes = ctrl.model.requests?.[0].posNotes || '';

        if (ctrl.model.company?.name) {
          ctrl.ordersSearch = '"' + ctrl.model.company.name + '"';
        }
      } else {
        // noop
      }

      if (CategoriesData.data && CategoriesData.data.success) {
        ctrl.accountCategories = CategoriesData.data.payload.list;

        if (ctrl.model.accountCategoryRelation) {
          const found = ctrl.findAccountCategory(ctrl.model.accountCategoryRelation.categoryName);
          ctrl.selectedAccountCategory = found?.category ?? null;
        }
      } else {
        // noop
      }

      ctrl.getPartnerPayments($state.params.id);
      ctrl.membershipsGridState.updateGridState(null, ctrl.membershipsGrid.pageSize, 'name');

      ctrl.gridDefinition.init(
        ctrl.partnersDetailOrdersListGridName,
        ctrl.ordersOptionsOverride,
        ctrl.viewId,
        $state.current.data.enablePreferences,
      );

      ctrl.gridDefinitionContacts.init(
        ctrl.partnersDetailContactsListGridName,
        ctrl.secondaryContactsOptionsOverride,
        ctrl.viewId,
        $state.current.data.enablePreferences,
      );

      ctrl.initFormsTabData();
      ctrl.formsGridDefinition.init(
        ctrl.formsGridName,
        ctrl.formsGridOptions.optionsOverride,
        ctrl.viewId,
        $state.current.data.enablePreferences,
      );

      if (permissionsService.check('FORM_RESPONSE_VIEW')) {
        ctrl.formsGridOptions.optionsOverride.columnDefs.unshift({
          field: 'View',
          headerName: 'View',
          width: 60,
          onCellNavClick: (row, col) => {
            ctrl.formsGridOptions.optionsOverride.appScopeProvider.onGridDoubleClick(row);
          },
          cellTemplate: `<div class="clickable ui-grid-cell-contents" ng-class="col.colIndex()"><a data-ng-click="grid.appScope.onGridDoubleClick(row)" role="link">View<span class="sr-only"> Details</span></a></div>`,
          enableHiding: false,
          enableSorting: false,
        });
      }

      $scope.$on('DataGrid::UserPreferencesLoaded', ctrl.initData);
      ctrl.getPartnerMemberships($state.params.id);
      ctrl.getPartnerMembershipAttributes($state.params.id);
    },

    initData: function () {
      const props = ctrl.gridDefinition.getGridState();
      const contactProps = ctrl.gridDefinitionContacts.getGridState();

      ctrl.initDateFormats();

      ctrl.ordersGridState.updateGridState({
        page: props.page,
        pageSize: props.pageSize,
        sortField: props.sortField,
        sortDirection: props.sortDirection,
      });
      ctrl.contactsGridState.updateGridState({
        page: contactProps.page,
        pageSize: contactProps.pageSize,
        sortField: contactProps.sortField,
        sortDirection: contactProps.sortDirection,
      });

      ctrl.formsGridState.updateGridState(ctrl.formsGridDefinition.getGridState());

      if (ctrl.gridDefinition.isAccessbilityEnabled) {
        ctrl.getPartnerOrders($state.params.id);
      }

      if (ctrl.gridDefinitionContacts.isAccessbilityEnabled) {
        ctrl.getPartnerContacts($state.params.id);
      }
    },

    initFormsTabData: function () { 
      ctrl.formsSelectedFilterDateType = ctrl.formsFilterDateType[0];
      ctrl.today = moment.tz(themesHelperService.getCompany('timezone'));
      ctrl.minDate = ctrl.today.clone().subtract(1, 'year');
      ctrl.maxDate = ctrl.today.clone();
      ctrl.formsQueryParams.filterType = ctrl.formsSelectedFilterDateType.type;
      ctrl.formsQueryParams.startDate = ctrl.minDate.clone().toISOString();
      ctrl.formsQueryParams.endDate = ctrl.maxDate.clone().toISOString();
    },

    initDateFormats: function () {
      ctrl.membershipsGrid.columns.push({
        field: 'membershipExpirationDate',
        displayName: 'Expires',
        headerTooltip: true,
        visible: true,
        cellFilter: 'date: "' + UserPreferencesService.getDateFormat('shortDate', 'dateFormat') + '"',
        cellFormatter: function (item) {
          return CalendarService.toFormat(item.value, UserPreferencesService.getDateFormat('shortDate', 'dateFormat'));
        },
      });

      ctrl.membershipsGrid.columns.push({
        field: 'membershipJoinedDate',
        displayName: 'Joined',
        headerTooltip: true,
        visible: true,
        cellFilter: 'date: "' + UserPreferencesService.getDateFormat('shortDate', 'dateFormat') + '"',
        cellFormatter: function (item) {
          return CalendarService.toFormat(item.value, UserPreferencesService.getDateFormat('shortDate', 'dateFormat'));
        },
      });

      ctrl.membershipsGrid.columns.push({
        field: 'membershipStartDate',
        displayName: 'Membership Start Date',
        headerTooltip: true,
        visible: true,
        cellFilter: 'date: "' + UserPreferencesService.getDateFormat('shortDate', 'dateFormat') + '"',
        cellFormatter: function (item) {
          return CalendarService.toFormat(item.value, UserPreferencesService.getDateFormat('shortDate', 'dateFormat'));
        },
      });
    },

    initPartnerPayments: function () {
      ctrl.isUpdatingPayment = true;
      ApiService.partners.payments
        .create(ctrl.payment)
        .then(() => {
          ctrl.isUpdatingPayment = false;
          ctrl.notice.showSuccessMessages([{ message: 'Sucessfully updated payment policy' }], { duration: 3000 });
        })
        .catch(() => {
          // noop
        });
    },

    getPartnerOrders: function (id: TODO) {
      ApiService.orders.partners
        .list({ ...ctrl.ordersGridState.getGridState(), id })
        .then((response) => {
          ctrl.orders = ctrl.processOrders(response.data.payload);
          ctrl.gridDefinition.isLoaded = true;
          $timeout(() => {
            $scope.$broadcast('DataGrid::ResizeGrid', {
              name: ctrl.gridDefinition.options.gridName,
              datasetLength: ctrl.orders.list.length,
              totalItems: ctrl.orders.pagination.count,
            });
          }, 10);
        })
        .catch(() => {
          // noop
        });
    },

    getPartnerForms: function () {
      this.notice.hideErrorMessages();
      ctrl.formsResponsesListStatus = 'pending';

      const organizationId = $state.params.id;
      const props = ctrl.formsGridDefinition.getGridState();

      partnersDetailService.fetchPartnerForms({
        organizationId: organizationId,
        ...props,
        filterType: ctrl.formsQueryParams.filterType,
        startDate: moment(ctrl.formsQueryParams.startDate).startOf('day').toISOString(),
        endDate: moment(ctrl.formsQueryParams.endDate).endOf('day').toISOString(),
      })
      .then(extractACMEPayload())
      .then((response) => {
        ctrl.formsResponsesList = response.list;
        ctrl.formsResponsesListStatus = 'fulfilled';
      })
      .catch((response: IHttpResponse<unknown>) => {
          this.notice.showErrorMessages([{ message: extractACMEErrorMessage(response.data) }]);
          this.formsResponsesList = [];
        })
        .finally(() => {
          ctrl.refreshFormGrid();
          ctrl.formsResponsesListStatus = 'fulfilled';
        });
    },

    hasForms: function () {
      return Array.isArray(ctrl.formsResponsesList) && ctrl.formsResponsesList.length > 0;
    },

    refreshFormGrid() {
      const totalItems = this.formsResponsesList ? this.formsResponsesList.length : 0;
      ctrl.formsGridDefinition.isLoaded = true;
      $timeout(() => {
        $scope.$broadcast('DataGrid::ResizeGrid', {
          name: ctrl.formsGridDefinition.options.gridName,
          datasetLength: totalItems,
          totalItems: totalItems,
        });
      }, 10);
    },

    getPartnerPayments: function (id) {
      ApiService.partners.payments
        .account(id)
        .then((response) => {
          console.log('PartnersDetailController::success::getPartnerPayments', response);
          const paymentType = response.data.payload;

          ctrl.payment.id = paymentType.id;
          ctrl.payment.type = paymentType.type;
          ctrl.payment.accountId = (paymentType.accountId || ctrl.model.company?.id) ?? null;
          ctrl.payment.uniqueVoucherNumbers = paymentType.uniqueVoucherNumbers;
          ctrl.payment.ticketDelivery = paymentType.ticketDelivery;
          ctrl.payment.taxExempt = paymentType.taxExempt;

          if (paymentType.balanceDue) {
            ctrl.payment.balanceDueDays = paymentType.balanceDue.days || 0;
          } else {
            ctrl.payment.balanceDueDays = 0;
          }

          if (paymentType.deposit) {
            ctrl.payment.depositDueDays = paymentType.deposit.daysBeforeEvent || 0;
            ctrl.payment.depositMinimumAmount = paymentType.deposit.minAmount || 0;
            ctrl.payment.depositMinimumPercent = paymentType.deposit.percent || 0;
          } else {
            ctrl.payment.depositDueDays = 0;
            ctrl.payment.depositMinimumAmount = 0;
            ctrl.payment.depositMinimumPercent = 0;
          }
        })
        .catch((response) => {
          if (response.status === 404) {
            ctrl.initPartnerPayments();
          }
        });
    },

    getPartnerContacts: function (id) {
      ctrl.gridDefinitionContacts.isLoaded = false;
      ApiService.organizations.contacts
        .list({ ...ctrl.contactsGridState.getGridState(), organizationId: id })
        .then((response) => {
          const pagination = response.data.payload.pagination;

          ctrl.secondaryContacts = response.data.payload.list;
          ctrl.contactsGridState.setPropertyValue('totalItems', response.data.payload.pagination.count);

          ctrl.isLoaded = true;
          ctrl.gridDefinitionContacts.isLoaded = true;

          $timeout(() => {
            $scope.$broadcast('DataGrid::ResizeGrid', {
              name: ctrl.gridDefinitionContacts.options.gridName,
              datasetLength: ctrl.secondaryContacts.length,
              totalItems: pagination.count,
            });
          }, 10);
        })
        .catch(() => {
          // noop
        });
    },

    getPartnerMemberships: function (id) {
      ApiService.organizations.membership
        .list({ id: id })
        .then((response) => {
          let paginated = false;
          let list: TODO[] = [];

          if (response.data.payload instanceof Array) {
            list = response.data.payload;
          } else if (response.data.payload.list instanceof Array) {
            list = response.data.payload.list;
            paginated = true;
          } else if (!response.data.payload.hasOwnProperty('pagination')) {
            list = [response.data.payload];
          }

          ctrl.memberships = list;
          ctrl.membershipsGridState.setPropertyValue(
            'totalItems',
            paginated ? response.data.payload.pagination.count : list.length,
          );
          ctrl.isLoaded = true;
        })
        .catch(() => {
          // noop
        });
    },

    getPartnerMembershipAttributes: function (id) {
      ApiService.organizations.membership
        .attributes({ ...ctrl.attributesGridState.getGridState(), id })
        .then((response) => {
          if (response.data.payload && response.data.payload.list instanceof Array) {
            ctrl.attributes = response.data.payload.list;
          } else {
            ctrl.attributes = [];
          }
          ctrl.isLoaded = true;
        })
        .catch(() => {
          // noop
        });
    },

    changeCategory: function () {
      ctrl.isUpdatingCategory = true;
    },

    updateCategory: function () {
      ctrl.isUpdatingCategory = false;
      console.log(
        'PartnersDetailController::updateCategory',
        ctrl.selectedAccountCategory,
        ctrl.model.accountCategoryRelation,
      );
      if (ctrl.model.accountCategoryRelation) {
        ApiService.partners.categories
          .reassign({
            id: ctrl.model.accountCategoryRelation.id,
            accountId: ctrl.model.accountCategoryRelation.accountId,
            categoryId: ctrl.selectedAccountCategory?.id,
          })
          .then((response) => {
            ctrl.model.accountCategoryRelation = response.data.payload;
          })
          .catch(() => {
            // noop
          });
      } else {
        invariant(ctrl.model.company);
        const company = ctrl.model.company;

        ApiService.partners.categories
          .assign({
            accountId: company.id,
            categoryId: ctrl.selectedAccountCategory?.id,
          })
          .then((response) => {
            ctrl.model.accountCategoryRelation = response.data.payload;
          })
          .catch(() => {
            // noop
          });
      }
    },

    cancelChangeCategory: function () {
      ctrl.isUpdatingCategory = false;
    },

    isTab: function (tab) {
      return ctrl.tab === tab;
    },

    setTab: function (tab) {
      ctrl.tab = tab;
      if (ctrl.tab === 'orders') {
        ctrl.getPartnerOrders($state.params.id);
      } else if (ctrl.tab === 'secondaryContacts') {
        ctrl.getPartnerContacts($state.params.id);
      } else if (ctrl.tab === 'memberships') {
        ctrl.getPartnerMemberships($state.params.id);
      } else if (ctrl.tab === 'attributes') {
        ctrl.getPartnerMembershipAttributes($state.params.id);
      } else if (ctrl.tab === 'forms') {
        ctrl.getPartnerForms();
      }
    },

    hasMemberships: function () {
      return Array.isArray(ctrl.memberships) && ctrl.memberships.length > 0;
    },

    hasAttributes: function () {
      return Array.isArray(ctrl.attributes) && ctrl.attributes.length > 0;
    },

    hasOrders: function () {
      return Array.isArray(ctrl.orders.list) && ctrl.orders.list.length > 0;
    },

    filterContacts: function (contacts: TODO[], primary: TODO) {
      return contacts.filter((contact) => contact.primary === primary);
    },

    updatePayment: function (payment) {
      ctrl.notice.hideErrorMessages();
      ctrl.notice.hideSuccessMessages();
      ctrl.isUpdatingPayment = true;
      ApiService.partners.payments
        .update(payment)
        .then(() => {
          ctrl.isUpdatingPayment = false;
          ctrl.notice.showSuccessMessages([{ message: 'Sucessfully updated payment policy' }], { duration: 3000 });
        })
        .catch((response) => {
          ctrl.isUpdatingPayment = false;
          ctrl.notice.showErrorMessages([{ message: response.data.error }]);
        });
    },

    createPayment: function (payment) {
      ctrl.notice.hideErrorMessages();
      ctrl.notice.hideSuccessMessages();
      ctrl.isUpdatingPayment = true;
      ApiService.partners.payments
        .create(payment)
        .then(() => {
          ctrl.isUpdatingPayment = false;
          ctrl.notice.showSuccessMessages([{ message: 'Sucessfully updated payment policy' }], { duration: 3000 });
        })
        .catch((response) => {
          ctrl.isUpdatingPayment = false;
          ctrl.notice.showErrorMessages([{ message: response.data.error }]);
        });
    },

    approve: function (requestId, index) {
      ApiService.partners
        .approve(requestId)
        .then((response) => {
          if (ctrl.model.requests) {
            ctrl.model.requests[index].status = response.data.payload.status;
          }
        })
        .catch(() => {
          // noop
        });
    },

    decline: function (requestId, index) {
      ApiService.partners
        .decline(requestId)
        .then((response) => {
          if (ctrl.model.requests) {
            ctrl.model.requests[index].status = response.data.payload.status;
          }
        })
        .catch(() => {
          // noop
        });
    },

    updateNotes: function () {
      ctrl.notice.hideErrorMessages();
      ctrl.notice.hideSuccessMessages();
      ApiService.partners.notes
        .update({
          id: ctrl.model.requests?.[0].requestId,
          notes: ctrl.notes,
        })
        .then(() => {
          ctrl.notice.showSuccessMessages([{ message: 'Sucessfully updated account notes' }], { duration: 3000 });
        })
        .catch((response) => {
          ctrl.notice.showErrorMessages([{ message: response.data.error }]);
        });
    },

    findAccountCategory: function (name: string) {
      const index = ctrl.accountCategories.findIndex((item) => item.name === name);

      return index === -1 ? undefined : { index, category: ctrl.accountCategories[index] };
    },

    processOrders: function (orders: ACMEListResponse<TODO>['payload']) {
      for (let i = 0, ii = orders.list.length; i < ii; i++) {
        orders.list[i].orderDate = CalendarService.toFormat(
          CalendarService.parseDate(orders.list[i].orderDate),
          UserPreferencesService.getDateFormat('shortDate', 'dateFormat'),
        );
      }

      return orders;
    },

    createOrder: function (model: PartnerDetail) {
      const organization = model.company;
      cartService.updateOrganization(organization);

      $state.go('application.programs.list');
    },

    createCustomEvent: function (model: PartnerDetail) {
      const organization = model.company;
      $state.go('application.orders.create', { accountId: organization?.id });
    },

    onOrdersDataGridPaginate: function (newPageNumber, newPageSize, sortField, sortDirection) {
      const data = {
        currentPage: newPageNumber || 1,
        pageSize: newPageSize || ctrl.gridDefinition.options.paginationPageSize,
      };

      ctrl.ordersGridState.updateGridState(data.currentPage, data.pageSize);
      if (sortField) {
        ctrl.ordersGridState.updateGridState(null, null, sortField, null);
      }
      if (sortDirection) {
        ctrl.ordersGridState.updateGridState(null, null, null, sortDirection);
      }

      ctrl.getPartnerOrders($state.params.id);
    },

    onOrdersDblClick: function (item) {
      $state.go('application.orders.detail.summary', {
        accountId: item.entity.tenantId,
        orderId: item.entity.orderId,
      });
    },

    onOrdersDataGridSortChange: function (grid, columns) {
      const data = columns[0];

      if (data) {
        ctrl.ordersGridState.updateGridState(null, null, data.field, data.sort.direction);
        ctrl.getPartnerOrders($state.params.id);
      }
    },

    onMembershipsGridDoubleClick: function (item) {
      $state.go('application.partners.detail.membership', { membershipId: item.entity.id });
    },

    onDataGridPaginate: function (newPage, pageSize, sortField, sortDirection) {
      ctrl.contactsGridState.updateGridState(newPage, pageSize);
      ctrl.getPartnerContacts($state.params.id);
    },

    onDataGridSortChange: function (grid, columns) {
      const data = columns[0];

      if (data) {
        ctrl.contactsGridState.updateGridState(null, null, data.field, data.sort.direction);
        ctrl.getPartnerContacts($state.params.id);
      }
    },
    onFormsFilterDateTypeChange(formsSelectedFilterDateType: { type: string; name: string }) {
      ctrl.formsQueryParams.filterType = formsSelectedFilterDateType.type;
    }
  };

  $scope.partnersDetail = ctrl;
  ctrl.init();
}

export const PartnersDetailControllerModule = angular
  .module('acme.view.application.partners.detail.controllers', [
    uiRouter,
    'angularUUID2',
    'acme.common.service.api',
    'acme.common.service.venue',
    'acme.common.service.helpers',
    'acme.common.service.calendar',
    'acme.common.service.userPreferences',
    CartServiceModule,
    'acme.common.factory.notice',
    'acme.common.factory.gridState',
    'acme.common.factory.gridDefinition',
    PartnersDetailServiceModule,
    DateRangeComponentModule,
    'acme.common.service.permissions',
    'acme.common.service.themesHelper',
  ])
  .controller('PartnersDetailController', PartnersDetailController).name;
